import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Tooltip, Tabs, Tab, CircularProgress
} from '@mui/material';
import { CheckCircle, RadioButtonUnchecked, Visibility, Edit, Lock } from '@mui/icons-material';

import { formatDateTime } from '../../utils/DiactiveUtils';
import { AdminRoles, DoctorRoles, PatientRoles, Phase, Role } from '../../config/DiactiveTypes';
import { fetchQuestionnaires } from '../../services/QuestionnaireService';
import SendEmailButton from '../common/SendEmailButton';
import PreEvalList from '../../data/questionnaires/PreEvalList.json';
import PostEvalList from '../../data/questionnaires/PostEvalList.json';

const tabNames = [
  "Evaluación PRE",
  "Evaluación POST"
];

const QuestionnairesTable = ({ patientId, patientRole, patientAge, authUserRole, phase, isPostEvalActive, isMobile, onSendReminderClick }) => {
  const initialPhaseTab = (phase === undefined || phase === Phase.POST) && isPostEvalActive ? Phase.POST : Phase.PRE;

  const navigate = useNavigate();

  const [phaseTab, setPhaseTab] = useState(initialPhaseTab);
  const [userQuestionnaires, setUserQuestionnaires] = useState([]);
  const [loading, setLoading] = useState(true);

  const isAdminRole = AdminRoles.includes(authUserRole);
  const isDoctorRole = DoctorRoles.includes(authUserRole);
  const isPatientRole = PatientRoles.includes(authUserRole);
  const isIntrPatient = patientRole === Role.Intr25;

  useEffect(() => {
    const fetchUserQuestionnaires = async () => {
      setLoading(true);
      const questionnaires = await fetchQuestionnaires(patientId);
      setUserQuestionnaires(questionnaires);
      setLoading(false);
    };
    fetchUserQuestionnaires();
  }, [patientId]);

  const onPhaseTabChange = (_, newValue) => {
    setPhaseTab(newValue);
  };

  const navigateToQuestionnaire = (questionnaireId) => {
    let route = `/questionnaire/${phaseTab}/${questionnaireId}`;
    if (isAdminRole) route += `/${patientId}`;
    navigate(route);
  };

  // Add this helper function before the return statement
  const getVisibleQuestionnaires = (evalList) => {
    let allPreviousComplete = true;
    let activeAssigned = false;

    return evalList
      .map((questionnaire) => {
        // Apply age/role filters
        if (questionnaire.onlyIntr && !isIntrPatient) return null;
        if (questionnaire.minAge && patientAge < questionnaire.minAge) return null;
        if (questionnaire.maxAge && patientAge >= questionnaire.maxAge) return null;

        const questionnaireData = {
          ...questionnaire
        };
        const userQuestionnaire = userQuestionnaires.find(q => q.questionnaireId === questionnaire.id && q.phase === phaseTab);
        questionnaireData.isComplete = userQuestionnaire !== undefined;
        if (questionnaireData.isComplete) {
          questionnaireData.createdAt = userQuestionnaire.createdAt;
        }

        // Calculate isNextToFill (Unity logic)
        const isNextToFill = !activeAssigned && allPreviousComplete && !questionnaireData.isComplete;
        if (isNextToFill) activeAssigned = true;

        // Update for next iteration
        allPreviousComplete = allPreviousComplete && questionnaireData.isComplete;

        return {
          questionnaireData,
          isNextToFill
        };
      })
      .filter(item => item !== null); // Remove filtered out questionnaires
  };

  const questionnairesList = getVisibleQuestionnaires(phaseTab === Phase.PRE ? PreEvalList : PostEvalList);
  const hasPendingQuestionnaire = questionnairesList.some(({ questionnaireData }) => !questionnaireData.isComplete);
  const showSendReminderButton = !loading && (isAdminRole || isDoctorRole) && hasPendingQuestionnaire;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {showSendReminderButton &&
        <SendEmailButton
          text="Recordar Cuestionarios"
          onClick={onSendReminderClick}
        />
      }
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Tabs
          value={phaseTab}
          onChange={onPhaseTabChange}
          variant={isMobile ? "scrollable" : "standard"}
          scrollButtons={isMobile ? "auto" : false}
          orientation={isMobile ? "horizontal" : "horizontal"}
          sx={{ mb: 3 }}
        >
          {tabNames.map((tabName, index) => (
            <Tab
              key={index}
              label={tabName}
              value={index === 0 ? Phase.PRE : Phase.POST}
              icon={index === 1 && !isPostEvalActive ? <Lock fontSize="10" /> : null}
              iconPosition={"start"}
              disabled={index === 1 && !isPostEvalActive}
              style={{fontSize: isMobile ? '0.75em' : '1em'}}
            />
          ))}
        </Tabs>
      </Box>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Fecha</TableCell>
              <TableCell>Cuestionario</TableCell>
              <TableCell>{isPatientRole ? "Responder" : "Respondido"}</TableCell>
              {
                isAdminRole &&
                <TableCell>Ver</TableCell>
              }
            </TableRow>
          </TableHead>
          <TableBody>
            {
              questionnairesList.map(({ questionnaireData, isNextToFill }) => (
                <QuestionnaireRow
                  key={questionnaireData.id}
                  questionnaire={questionnaireData}
                  isAdminRole={isAdminRole}
                  isPatientRole={isPatientRole}
                  isNextToFill={isNextToFill}
                  onClick={navigateToQuestionnaire}
                />
              ))
            }
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

const QuestionnaireRow = ({ questionnaire, isAdminRole, isPatientRole, isNextToFill, onClick }) => {
  return (
    <TableRow key={questionnaire.id}>
      <TableCell sx={{ width: 150 }}>
        {questionnaire.createdAt ? formatDateTime(questionnaire.createdAt) : "–"}
      </TableCell>
      <TableCell sx={{ maxWidth: 500 }}>{questionnaire.name}</TableCell>
      {
        isPatientRole &&
        <TableCell>
          {
            questionnaire.isComplete &&
            statusIcon(questionnaire.isComplete)
          }
          {
            !questionnaire.isComplete &&
            <IconButton onClick={() => onClick(questionnaire.id)} disabled={!isNextToFill}>
              <Edit color={isNextToFill ? "primary" : "disabled"} />
            </IconButton>
          }
        </TableCell>
      }
      {
        !isPatientRole &&
        <TableCell>{statusIcon(questionnaire.isComplete)}</TableCell>
      }
      {
        isAdminRole &&
        <TableCell>
          <IconButton onClick={() => onClick(questionnaire.id)} disabled={!questionnaire.isComplete}>
            <Visibility color={questionnaire.isComplete ? "primary" : "disabled"} />
          </IconButton>
        </TableCell>
      }
    </TableRow>
  );
};

const statusIcon = (isComplete) => {
  if (isComplete) {
    return <Tooltip title="Completado"><CheckCircle color="success" /></Tooltip>;
  }
  else {
    return <Tooltip title="No iniciado"><RadioButtonUnchecked color="disabled" /></Tooltip>;
  }
};

export default QuestionnairesTable;